/**
 * flow — 단계형 화면(초기 설정·진단)의 순수 단계 머신.
 *
 * 단계 목록은 **플로우 설정**(`FlowConfig`)이 갖고, 리듀서·선택자는 설정을 받아 돈다.
 * 두 플로우가 같은 셸(StepShell)·같은 컨텍스트를 쓰되 단계·드래프트 키·닫기 가능 여부만
 * 다르기 때문이다 — 목록을 두 벌 복제하면 셸이 어느 목록을 보는지가 두 곳에서 갈린다.
 *
 * React·시간·네이티브 의존 없음 — 같은 입력은 같은 결과(결정론). vitest 대상.
 */

export type OnboardingStepId =
  | 'nick'
  | 'gender'
  | 'birthYear'
  | 'experience'
  | 'style'
  | 'purpose'
  | 'environment'
  | 'condition'
  | 'surveyDone'
  | 'diagnosisIntro'
  | 'record'
  | 'result';

export interface FlowConfig {
  id: 'setup' | 'diagnosis';
  /** 화면 진행 순서. 진행 바의 분모 = 이 배열 길이. */
  steps: readonly OnboardingStepId[];
  /** 진행 위치·답을 살려 두는 localStorage 키 — 플로우마다 다르다. */
  draftKey: string;
  /** `×`로 나갈 수 있나. 초기 설정은 프로필 없이는 갈 곳이 없어 false. */
  closable: boolean;
}

/**
 * 초기 설정(시안 `758:20382` ~ `758:20959`) — 설문 8단계 + 완료 안내.
 * 닉네임이 맨 앞이다: 이후 단계 제목이 그 이름을 부른다("<닉네임>님의 나이를…").
 * 측정환경·컨디션도 시안대로 여기서 한 번 묻는다 — 회차마다 달라지는 값이라 진단
 * 플로우가 다시 묻는다(DIAGNOSIS_FLOW).
 */
export const SETUP_FLOW: FlowConfig = {
  id: 'setup',
  steps: [
    'nick',
    'gender',
    'birthYear',
    'experience',
    'style',
    'purpose',
    'environment',
    'condition',
    'surveyDone',
  ],
  draftKey: 'muzly.setup.draft.v2',
  closable: false,
};

/**
 * 진단 회차 — 홈의 `진단하기`가 연다. 회차마다 달라지는 답 둘이 녹음 직전에 온다:
 * 답이 가리켜야 하는 건 곧 있을 그 녹음이지, 설정을 채우던 시점이 아니다.
 * 성별·나이 같은 프로필 답은 컨텍스트가 프로필에서 합쳐 준다 — 여기서 다시 묻지 않는다.
 */
export const DIAGNOSIS_FLOW: FlowConfig = {
  id: 'diagnosis',
  steps: ['environment', 'condition', 'diagnosisIntro', 'record', 'result'],
  draftKey: 'muzly.diagnosis.draft.v2',
  closable: true,
};

/** 단일 선택·자유입력은 string, 다중 선택은 string[]. */
export type OnboardingAnswer = string | string[];

export interface OnboardingState {
  /** `flow.steps` 내 현재 위치. */
  index: number;
  /** 단계 id → 응답. */
  answers: Record<string, OnboardingAnswer>;
}

export type OnboardingEvent =
  | { type: 'next' } // 다음 단계로
  | { type: 'back' } // 이전 단계로
  | { type: 'answer'; id: string; value: OnboardingAnswer } // 응답 기록(인덱스 불변)
  | { type: 'reset' }; // 처음으로

export const INITIAL_ONBOARDING_STATE: OnboardingState = {
  index: 0,
  answers: {},
};

const lastIndex = (flow: FlowConfig): number => flow.steps.length - 1;

/**
 * 단계 전이. next/back은 `[0, last]`로 클램프하고, 끝에서 막히면 원래 상태 참조를
 * 그대로 반환한다(React 불필요 리렌더 방지). answer는 인덱스를 건드리지 않고 응답만
 * 병합한다. 플로우를 먼저 받는 커링이라 `useReducer(onboardingReducer(flow), …)`로 쓴다.
 */
export const onboardingReducer =
  (flow: FlowConfig) =>
  (state: OnboardingState, event: OnboardingEvent): OnboardingState => {
    switch (event.type) {
      case 'next':
        return state.index >= lastIndex(flow)
          ? state
          : { ...state, index: state.index + 1 };
      case 'back':
        return state.index <= 0 ? state : { ...state, index: state.index - 1 };
      case 'answer':
        return {
          ...state,
          answers: { ...state.answers, [event.id]: event.value },
        };
      case 'reset':
        return INITIAL_ONBOARDING_STATE;
    }
  };

/** 현재 단계 id. */
export const currentStepId = (
  flow: FlowConfig,
  state: OnboardingState,
): OnboardingStepId => flow.steps[state.index];

export const isFirstStep = (state: OnboardingState): boolean =>
  state.index <= 0;

export const isLastStep = (flow: FlowConfig, state: OnboardingState): boolean =>
  state.index >= lastIndex(flow);

/** 진행 바 표시용 — current는 1-기준(1 / total). */
export const stepProgress = (
  flow: FlowConfig,
  state: OnboardingState,
): { current: number; total: number } => ({
  current: state.index + 1,
  total: flow.steps.length,
});
